import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { apiRequest } from "../api/client";
import { Button, Checkbox, Input } from "../ds";
import { isTextValueSelected, toggleTextValue, type BasicTextSelection } from "./basicTextFilter";

interface ValuesResponse { values: string[]; total: number; has_more: boolean; truncated: boolean; status: string }

export function BasicTextFilterPicker({ datasetId, field, selection, onChange }: {
  datasetId: string;
  field: string;
  selection: BasicTextSelection;
  onChange: (selection: BasicTextSelection) => void;
}) {
  const [search, setSearch] = useState("");
  const [needle, setNeedle] = useState("");
  useEffect(() => { const timer = window.setTimeout(() => setNeedle(search), 250); return () => window.clearTimeout(timer); }, [search]);
  const values = useQuery({
    queryKey: ["studio-filter-values", datasetId, field, needle],
    queryFn: () => apiRequest<ValuesResponse>(`/api/v1/owner/datasets/${datasetId}/filter-values?field=${encodeURIComponent(field)}&search=${encodeURIComponent(needle)}`),
    staleTime: 60_000,
    retry: 1,
  });
  const options = values.data?.values ?? [];
  const unavailable = values.isError || values.data?.status === "unavailable";
  const summary = selection.all ? selection.values.length ? `All except ${selection.values.length} items` : "All items selected" : `${selection.values.length} items selected`;
  return <details open className="st-filter-values" style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 8 }}>
    <summary aria-label="Item selection" style={{ cursor: "pointer", fontSize: 12.5 }}>{summary}</summary>
    <Input inputSize="sm" aria-label="Search items" placeholder="Search items…" maxLength={300} value={search} onChange={(e) => setSearch(e.target.value)} style={{ marginTop: 8, marginBottom: 8 }} />
    <Checkbox label="Select all" checked={selection.all && !selection.values.length} onChange={(checked) => onChange({ all: checked, values: [] })} />
    <div style={{ borderTop: "1px solid var(--border)", maxHeight: 220, overflowY: "auto", marginTop: 6, paddingTop: 6 }}>
      {options.map((value) => <Checkbox key={value} label={value === "" ? "(Empty text)" : value} checked={isTextValueSelected(selection, value)} onChange={(checked) => onChange(toggleTextValue(selection, value, checked))} />)}
    </div>
    {values.isFetching ? <div className="ds-small ds-muted">Loading items…</div> : null}
    {unavailable ? <div className="ds-small ds-muted">Couldn’t load items. <Button size="sm" onClick={() => void values.refetch()}>Retry</Button></div> : null}
    {!values.isPending && !unavailable && !options.length ? <div className="ds-small ds-muted">No matching items.</div> : null}
    {values.data?.has_more ? <div className="ds-small ds-muted">Showing {options.length} of {values.data.total} matching items. Search to find more.</div> : null}
    {selection.values.length ? <div className="ds-small ds-muted">{selection.all ? "Unchecked:" : "Checked:"} {selection.values.map(v => v === "" ? "(Empty text)" : v).join(", ")}</div> : null}
    <div className="ds-small ds-faint" style={{ marginTop: 6 }}>Select all applies to the whole column. Untick items to hide them.</div>
    {values.data?.status === "delayed" ? <div className="ds-small ds-muted">Showing saved items; the source could not refresh.</div> : values.data?.truncated ? <div className="ds-small ds-muted">Some source values may be missing from this list.</div> : null}
  </details>;
}
